import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import Button from '../../Input/Button/Button';
import CommonCardShell from '../../Layout/CommonCardShell/CommonCardShell';

import './LanderChoice.scss';

export default class LanderChoice extends React.PureComponent {
    constructor(props) {
        super();

        this.state = {
            style: {},
            isInTransition: false,
        };

        this.baseCls = 'component-Groups-LanderChoice';
        this.headerRef = React.createRef();
        this.watcher = null;
    }

    static propTypes = {
        img: PropTypes.shape({
            src: PropTypes.string,
            alt: PropTypes.string,
        }),
        headline: PropTypes.string.isRequired,
        bodyCopy: PropTypes.string.isRequired,
        cta: PropTypes.shape({
            path: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
        }).isRequired,
        childRef: PropTypes.object,
        routeData: PropTypes.object.isRequired,
        isOpen: PropTypes.bool.isRequired,
        metricsType: PropTypes.string.isRequired,
        resizeContainer: PropTypes.func.isRequired,
        containerStyle: PropTypes.object,
    };

    static defaultProps = {
        img: {
            src: null,
            alt: '',
        },
        childRef: null,
    };

    componentDidMount = () => {
        const { childRef } = this.props;
        if (childRef && childRef.current && this.props.isOpen) {
            // if we loaded on an opened card, start the watcher.
            this.watchContainerHeight();
        }
    };

    componentWillUnmount = () => {
        window.cancelAnimationFrame(this.watcher);
    };

    updateCardSize = (element = null, childIsAppearing = false) => {
        const { headerRef, childRef } = this.props;

        const child = element || childRef.current;

        if (childIsAppearing) {
            this.setState({
                style: {
                    height: `${child.clientHeight + 40}px`,
                },
            });
        } else if (headerRef) {
            this.setState({
                style: {
                    height: `${headerRef.current.clientHeight + 40}px`,
                },
            });
        } else {
            this.setState({
                style: {},
            });
        }
    };

    componentDidUpdate = () => {
        const {
            isOpen,
            childRef,
            routeData: { history },
        } = this.props;

        if (isOpen && !this.watcher) {
            this.handleOpen();
        } else if (!isOpen && history.action === 'POP' && this.watcher) {
            this.handleClose(childRef.current);
        }
    };

    watchContainerHeight = () => {
        if (!this.props.childRef.current) {
            return;
        }

        const { childRef, isOpen, extraRef } = this.props;

        if (
            `${
                childRef.current.clientHeight +
                ((extraRef &&
                    extraRef.current &&
                    extraRef.current.clientHeight) ||
                    0) +
                40
            }px` !==
            (this.props.containerStyle && this.props.containerStyle.height)
        ) {
            this.updateContainerSize(childRef.current, true);
        }

        if (isOpen) {
            this.watcher = window.requestAnimationFrame(
                this.watchContainerHeight
            );
        } else {
            this.updateContainerSize(childRef.current);
        }
    };

    handleOpen = () => {
        this.watchContainerHeight();
    };

    handleLinkClick = () => {
        this.handleMetrics(this.props.metricsType);
        this.handleOpen();
    };

    updateContainerSize = (element, isAppearing = false) => {
        const { resizeContainer, extraRef } = this.props;
        if (element) {
            resizeContainer(
                isAppearing
                    ? element.clientHeight +
                          ((extraRef &&
                              extraRef.current &&
                              extraRef.current.clientHeight) ||
                              0) +
                          40
                    : null
            );
        }
        this.updateCardSize(element, isAppearing);
    };

    expandContainer = (element) => this.updateContainerSize(element, true);

    handleClose = async (element = null) => {
        await window.cancelAnimationFrame(this.watcher);
        this.watcher = null;
        this.updateContainerSize(element);
    };

    onTransitionStart = async () => {
        await this.setState({ isInTransition: true });
    };

    onTransitionEnd = async () => {
        await this.setState({ isInTransition: false });
    };

    onChildEnter = async () => {
        await this.onTransitionStart();
        this.expandContainer();
    };

    onChildExit = async () => {
        await this.onTransitionStart();
        this.handleClose();
    };

    handleMetrics = (type) => {
        if (type === 'create-group') {
            return EmitMetric({
                misc_event_type: 'create-group-master-lander-click',
                misc_event_count: 1,
            });
        } else if (type === 'search-groups') {
            return EmitMetric({
                misc_event_type: 'search-groups-master-lander-click',
                misc_event_count: 1,
            });
        }
    };

    render() {
        const {
            baseCls,
            headerRef,
            props: {
                handle,
                children,
                img,
                bodyCopy,
                headline,
                cta,
                routeData: {
                    location: { pathname },
                },
                transitionDuration,
            },
        } = this;

        const image =
            (img.src && (
                <img
                    className={`${baseCls}__icon`}
                    src={img.src}
                    alt={img.alt}
                />
            )) ||
            null;
        let className = baseCls;

        if (this.props.isOpen) {
            className += ` ${baseCls}--open`;
        }

        return (
            <CommonCardShell
                className={className}
                style={{ ...this.state.style }}
            >
                <TransitionGroup
                    className={`${baseCls}__inner ${
                        this.state.isInTransition
                            ? `${baseCls}__inner--in-transition`
                            : ''
                    }`}
                >
                    {pathname !== cta.path && (
                        <CSSTransition
                            key={`${handle}-header`}
                            timeout={transitionDuration}
                            onEnter={this.onTransitionStart}
                            onEntered={this.onTransitionEnd}
                            onExit={this.onTransitionStart}
                            onExited={this.onTransitionEnd}
                        >
                            <header
                                ref={headerRef}
                                className={`${baseCls}__header`}
                            >
                                <Link
                                    to={cta.path}
                                    className={`${baseCls}__link-wrapper`}
                                    onClick={this.handleLinkClick}
                                >
                                    <div
                                        className={`${baseCls}__header-content`}
                                    >
                                        {image}
                                        <h2 className={`${baseCls}__title`}>
                                            {headline}
                                        </h2>
                                        <p className={`${baseCls}__text`}>
                                            {bodyCopy}
                                        </p>
                                        <Button
                                            className={`${baseCls}__cta`}
                                            type={'wired'}
                                            value={cta.text}
                                        />
                                    </div>
                                </Link>
                            </header>
                        </CSSTransition>
                    )}
                    {pathname === cta.path && (
                        <CSSTransition
                            key={`${handle}-content`}
                            timeout={transitionDuration}
                            onEnter={this.onChildEnter}
                            onEntered={this.onTransitionEnd}
                            onExit={this.onChildExit}
                            onExited={this.onTransitionEnd}
                        >
                            {children}
                        </CSSTransition>
                    )}
                </TransitionGroup>
            </CommonCardShell>
        );
    }
}
